import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { joinClassNames } from '../../util/util';

// A URL inside the description, which is where the "read more" link lives - there is no separate
// field for it. Bare www. forms count, and trailing punctuation belongs to the sentence.
const URL_IN_TEXT = /(https?:\/\/[^\s<]+|www\.[^\s<]+)/gi;

// Focus is moved into the feed with preventScroll - the chrome is position: fixed and a scroll
// there moves the page, not the text - so a link below the fold of the open description would take
// focus without ever coming into view. This brings it in, and only it: `nearest` leaves any
// ancestor that already shows the link alone.
const showOnFocus = (event: React.FocusEvent<HTMLAnchorElement>) =>
	event.currentTarget.scrollIntoView({ block: 'nearest' });

const linkify = (text: string, tabIndex: number) =>
	text.split(URL_IN_TEXT).map((part, index) => {
		if (!URL_IN_TEXT.test(part)) {
			URL_IN_TEXT.lastIndex = 0;
			return part;
		}
		URL_IN_TEXT.lastIndex = 0;
		const trimmed = part.replace(/[.,;:!?)\]]+$/, '');
		const tail = part.slice(trimmed.length);
		return (
			<React.Fragment key={`${trimmed}-${index}`}>
				<a
					className="qplayer-story-info__link"
					href={trimmed.startsWith('http') ? trimmed : `https://${trimmed}`}
					target="_blank"
					rel="noopener noreferrer"
					// The text around it toggles; the link must not.
					onClick={(event) => event.stopPropagation()}
					onFocus={showOnFocus}
					tabIndex={tabIndex}
				>
					{trimmed}
				</a>
				{tail}
			</React.Fragment>
		);
	});

// One per mounted clip, for the hidden hint below. React 17 has no useId.
let hintSeed = 0;

interface StoryClipInfoProps {
	description: string;
	/** The clip on screen. Only its text is reachable by keyboard - see the tabIndex below. */
	isActive?: boolean;
}

/**
 * A clip's description, over the bottom of the card. Feed only.
 *
 * The title is deliberately not drawn: the design carries one block of text, and a heading above a
 * description read as two competing voices on a 9:16 card. The media's title is still reported on
 * `storyClipChanged`, which is where a host reads it from.
 */
const StoryClipInfo = ({ description, isActive = true }: StoryClipInfoProps) => {
	const { t } = useTranslation();
	const [isExpanded, setIsExpanded] = useState(false);
	// Measured, not guessed from a character count: whether two lines are enough depends on the
	// card's width and the viewer's font size.
	const [doesOverflow, setDoesOverflow] = useState(false);
	const descriptionRef = useRef<HTMLParagraphElement | null>(null);
	// useState's lazy form, not useRef: a useRef argument is re-evaluated on every render, so the
	// id would be stable but the counter behind it would climb forever.
	const [hintId] = useState(() => `qplayer-story-info-hint-${(hintSeed += 1)}`);

	useEffect(() => {
		const element = descriptionRef.current;
		setDoesOverflow(!!element && element.scrollHeight > element.clientHeight + 1);
	}, [description]);

	// Everything the keyboard does to the text, in one native listener on the element itself. Native
	// rather than React's, and on the element rather than an ancestor, because every key here is one
	// the feed has already claimed on the document - and the element is the first thing the event
	// passes through, so stopPropagation settles it before the feed ever sees it.
	useEffect(() => {
		const element = descriptionRef.current;
		if (!element) {
			return undefined;
		}
		const onKeyDown = (event: KeyboardEvent) => {
			// Enter and Space open and close the text. There is no separate control to carry that
			// any more - the text is the control - so both have to be taken off the feed: Space is
			// its play/pause shortcut, bound to the document and deliberately NOT skipped over
			// anything that is merely a region.
			if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
				event.preventDefault();
				event.stopPropagation();
				setIsExpanded((open) => !open);
				return;
			}
			// The arrows step the feed, which means an open description could be focused but never
			// read past its first screen. While it is open they are the text's.
			//
			// Unconditional, deliberately. An earlier version handed the key back at either end of
			// the text so a viewer who had read to the bottom could carry on with the same key -
			// and in use that reads as the player skipping a clip on its own, because the text
			// running out is not something the viewer is watching for. Tab is the way out.
			if (isExpanded && (event.key === 'ArrowUp' || event.key === 'ArrowDown')) {
				event.stopPropagation();
			}
		};
		// Only while it is open: collapsed, there is nothing to scroll, and swallowing the wheel
		// there would stop the feed moving under the pointer.
		const keepScroll = (event: Event) => event.stopPropagation();
		element.addEventListener('keydown', onKeyDown);
		if (isExpanded) {
			element.addEventListener('wheel', keepScroll, { passive: true });
		}
		return () => {
			element.removeEventListener('keydown', onKeyDown);
			element.removeEventListener('wheel', keepScroll);
		};
	}, [isExpanded]);

	if (!description) {
		return null;
	}

	// Every clip's text is in the DOM, on screen or not, so without this the tab order walks the
	// whole playlist: from this clip's description to the next clip's, and the next, never reaching
	// the controls of the clip being watched. -1 rather than unmounting, because the text still has
	// to be there to paint and to measure its own overflow.
	const tabIndex = isActive ? 0 : -1;

	return (
		// The whole block toggles, not just the words. A tap beside the text - in the margin, above
		// the first line, out towards the card's edge - is a tap on the description as far as the
		// viewer is concerned, and it used to fall through to the stage and pause the clip instead.
		// stopPropagation still matters: it is what keeps the stage from hearing the same tap.
		<div
			className="qplayer-story-info"
			onClick={(event) => {
				event.stopPropagation();
				setIsExpanded((open) => !open);
			}}
		>
			{description ? (
				<p
					ref={descriptionRef}
					// The text is the control. There is no "Vis mere" button beside it anymore: the
					// block has always opened on a tap anywhere in it, and a second tab stop whose only
					// job was to say so is one more place for a keyboard to get lost - which is what
					// the audit found. Enter and Space on the text do what the tap does.
					//
					// role="region" rather than "button", for two reasons worth keeping. A button's
					// children are presentational, so the links inside the description would vanish
					// from the accessibility tree; and aria-expanded is not allowed on a region, so the
					// state is carried by the hint below instead, which changes wording with it.
					// Named, because a focusable scrollable box with no name is announced as nothing.
					tabIndex={tabIndex}
					role="region"
					aria-label={t('LABEL_DESCRIPTION')}
					aria-keyshortcuts="Enter"
					aria-describedby={hintId}
					className={joinClassNames(
						'qplayer-story-info__text',
						// Swiper's own opt-out class: a drag starting here scrolls the text, not the feed.
						isExpanded ? 'qplayer-story-info__text--open swiper-no-swiping' : ''
					)}
				>
					{/* Collapsed, the clamp cuts the text off after two lines, and what is past them is
					    still painted - just clipped, often right off the card. A link down there is a tab
					    stop the viewer cannot see, which is how tabbing through the feed loses track of
					    where the focus went. Reachable only once nothing is hidden: expanded, or short
					    enough that the clamp never bit. Enter on the text is what opens the rest. */}
					{linkify(description, isExpanded || !doesOverflow ? tabIndex : -1)}
				</p>
			) : null}
			{/* The strip between the last line and the control bar. It looks like part of the
			    picture but reads as part of the text, so a tap that lands a little low opens the
			    description instead of pausing the clip. Not a button: it draws nothing and says
			    nothing, and the text above carries the whole interaction for assistive tech. */}
			<div className="qplayer-story-info__tap" aria-hidden="true" />
			{/* Never drawn, never focused, never in the tab order - it exists only to be read out
			    as the text's description. Without it a screen reader announces "Beskrivelse,
			    region" and nothing at all about Enter doing anything, which is what the removed
			    button used to say. Its wording flips with the state, so it reports that too. */}
			<span id={hintId} className="qplayer-story-info__hint">
				{t(isExpanded ? 'LABEL_SHOW_LESS' : 'LABEL_SHOW_MORE')}
			</span>
		</div>
	);
};

export default StoryClipInfo;
